// src/components/DealsTab.jsx
// Deals / Viewings pipeline — the Results-pillar front end. Admin finds a property by ID/
// cadastral/link (api.sentavus.com → project-B drafts), imports its commission, records the
// agent(s) + client + owner, and tracks the deal through its lifecycle with a timeline.
// One record = one property + agent(s); repeat visits append to the same record. See db/index.js.
import { useState, useEffect, useMemo, useRef } from 'react'
import { useDeliberateBackdropDismiss } from '../hooks/useDeliberateBackdropDismiss.js'
import {
  addViewing, updateViewing, deleteViewing, addViewingEvent, deleteViewingEvent, searchDrafts,
  snapshotViewingCommissions, getMyRole, getOfficeHistory,
} from '../db/index.js'

// ⚠️ A STAMP IS PERMANENT, SO IT MUST BE RESOLVED AS-OF THE CLOSE DATE, NOT AS-OF NOW.
// managerLinksForDeal reads the roster's CURRENT office, rate and is_active unless it is given
// the dated tables (scheduler 0001 / 0007 / 0025). That is right for a deal closing today and
// wrong for every other case: a deal created already closed, a close whose payment date is in
// the past, or any re-stamp after a reorg — after 2026-08-19 an undated re-stamp names Luka
// Bigvava on every historical Delisi deal, and he was a Senior Agent for the whole period those
// deals belong to. The same disease was already fixed once on this screen for commission_pct
// (see the closingNow comment below); manager_links had it too.
// Fetched at stamp time rather than held in state on purpose: closing is a deliberate and
// infrequent act, so one small read costs nothing, while keeping it in state would put an extra
// fetch on every mount of a screen that mostly does not close anything.
// Soft-fails to null, which restores the pre-2026-09-20 behaviour rather than blocking a close.
async function stampOpts(dateStr) {
  const history = await getOfficeHistory().catch(() => null)
  return { date: dateStr || null, history }
}

import { getMeetingsFeed, searchCoreListings, reportDealClosure, listBinders, getPropertyThumbs, getDistricts, getFeatures, getMeetingNudges } from '../db/core.js'
import { MeetingCardGrid } from './desk/MeetingsFeed.jsx'
import { CoreMeetingDetail, MeetingModal } from './desk/MeetingModal.jsx'
import { useAuth } from '../hooks/useAuth.js'
import { getCurrentAdmin } from '../lib/agentLog.js'
import { managerLinksForDeal } from '../lib/performance.js'
import { MEETING_STATUSES } from '../../core/meeting-statuses.mjs'
// ⚠️ THE WRITE HALF WAS FIXED IN 2026-08 AND THE READ HALF WAS NOT (corrected 2026-08-15).
// This file imported `composeTbilisi` only, so a meeting written as 15:00 Tbilisi was read
// back at the DEVICE's wall clock — every date and time on every row, card and timeline in
// this tab. Same ruling, same helper as the four files that already use it.
import { composeTbilisi, tbilisiParts, fmtTbilisi } from '../lib/tbilisiTime.js'
import { DateInput } from './DateInput.jsx'
import { SingleSelect, ViewToggle } from './AgentsTab.jsx'
import { useViewPreference } from '../hooks/useViewPreference.js'
import { t, tLog, getLanguage } from '../lib/i18n.js'
import { Icon } from './Icon.jsx'
import { COUNTER, pill } from './desk/primitives.jsx'
import { TimeInput } from './SegmentedInput.jsx'

const inp = 'w-full rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring'
const lbl = 'text-xs font-medium text-muted-foreground block mb-1'

// Every stored meeting status, core included (0392 'rescheduled', 0396 'cancelled') — this
// list drives the status filter, so a value missing here is a deal nobody can filter to.
// ⚠️ 'viewing' is a LEGACY ALIAS still stored on old rows — STATUS_BADGE below keeps it, QUICK
// keeps it, and it has its own i18n string, but it was missing HERE, so any deal in that
// status was counted in "all", reachable by no filter, and the invariant this comment states
// was false (fixed 2026-08-15). Last, because it is history rather than a status anyone sets.
// ⚠️ WHAT YOU MAY SET AND WHAT YOU MAY FIND ARE DIFFERENT LISTS, and merging them was a bug.
// Until 2026-09-20 this was ONE array containing 'viewing', a legacy alias, and it fed BOTH the
// status picker and the filter dropdown. 'viewing' is in neither MEETING_ACTIVE nor
// MEETING_FINISHED, so saving a meeting with it put the row in NO Desk view — the same
// disappearance that `rescheduled` caused on 2026-08-03 and that core/meeting-statuses.mjs
// exists to prevent. Nothing had ever been saved with it (0 rows), but the button was live.
//
// SETTABLE = the registry, so a status a human can choose is always in one of the two lists.
// FINDABLE = settable + legacy aliases, so a historical row stays reachable by the filter. A
// value you can no longer write must still be one you can look for, or the row is lost.
const SETTABLE_STATUSES = MEETING_STATUSES
const LEGACY_STATUS_ALIASES = ['viewing']
const FINDABLE_STATUSES = [...MEETING_STATUSES, ...LEGACY_STATUS_ALIASES]
const STATUS_BADGE = {
  scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  viewing:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', // legacy alias
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  binder:  'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  closed:  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  failed:  'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  // Core-only states (0392/0396). Grey for both: one is a non-outcome and the other is an
  // interruption — neither is a result, so neither earns a colour that claims to be one.
  cancelled:   'bg-muted text-muted-foreground',
  rescheduled: 'bg-muted text-muted-foreground',
}
// Quick-action buttons per current status: [eventKind, newStatus]
const QUICK = {
  scheduled: [['occurred', 'pending'], ['no_show', 'failed'], ['rejected', 'failed'], ['reschedule', 'scheduled']],
  viewing:   [['occurred', 'pending'], ['no_show', 'failed'], ['rejected', 'failed'], ['reschedule', 'scheduled']],
  pending:   [['binder', 'binder'], ['closed', 'closed'], ['rejected', 'failed']],
  binder:    [['closed', 'closed'], ['cancelled', 'failed']],
  closed:    [],
  failed:    [['reschedule', 'scheduled']],
}

function fmtMoney(n, cur = 'GEL') {
  if (n == null || n === '') return '—'
  const sym = cur === 'USD' ? '$' : cur === 'EUR' ? '€' : '₾'
  return sym + Math.round(Number(n)).toLocaleString('en-US')
}
function dealTypeLabel(dt) {
  return dt === 'trade' ? t('dv_deal_type_trade') : dt === 'lease' ? t('dv_deal_type_lease') : (dt || '—')
}
function fmtDate(iso) {
  if (!iso) return ''
  return fmtTbilisi(iso + (iso.length <= 10 ? 'T12:00:00' : ''),
    { day: 'numeric', month: 'short', year: '2-digit' })
}
// Date + time from an event (event_at preferred; falls back to event_date)
function fmtDateTime(e) {
  const at = e?.event_at || e?.event_date
  if (!at) return ''
  const iso = at + (at.length <= 10 ? 'T12:00:00' : '')
  const date = fmtTbilisi(iso, { day: 'numeric', month: 'short' })
  return e?.event_at ? `${date} ${fmtTbilisi(iso, { hour: '2-digit', minute: '2-digit' })}` : date
}

// Commission is type-aware (mirrors the draft editor's პროც/ფიქსირებული/ხელზე):
//   percent → % of price | fixed → flat amount | khelze → (trade only) owner takes X, commission = price − X.
// Import priority fixed → percent → khelze: khelze (price_client) is dirty real data — some agents type a
// small fixed fee into the ხელზე box — so it's last and the admin always reviews the computed amount.
function resolveCommission(d) {
  const unit = d.price_unit || 'GEL'
  if (d.price_agent != null && Number(d.price_agent) > 0)
    return { type: 'fixed', pct: '', khelze: '', fixed: Number(d.price_agent), currency: unit, source: 'imported' }
  if (d.price_pct != null && Number(d.price_pct) > 0)
    return { type: 'percent', pct: Number(d.price_pct), khelze: '', fixed: '', currency: unit, source: 'imported' }
  if (d.deal_type === 'trade' && d.price_client != null && Number(d.price_client) > 0)
    return { type: 'khelze', pct: '', khelze: Number(d.price_client), fixed: '', currency: unit, source: 'imported' }
  return { type: 'percent', pct: '', khelze: '', fixed: '', currency: unit, source: 'manual' }
}

// One resolved number, in the deal currency. percent: lease=of monthly rent, trade=of sale price.
function computeCommission(type, price, pct, fixed, khelze) {
  const p = Number(price) || 0
  if (type === 'fixed')  return Number(fixed) || 0
  if (type === 'khelze') return Math.max(0, p - (Number(khelze) || 0))
  return Math.round(p * (Number(pct) || 0) / 100)
}

// Phone is the client key (per user: same number = same client) until a clients table exists.
const normPhone = p => (p || '').replace(/\D/g, '')

// Draft owner email → scheduler agent (login_id is the canonical key; email is the fallback).
function matchOwnerAgent(agents, email) {
  if (!email) return null
  const lc = email.toLowerCase(), prefix = lc.split('@')[0]
  return agents.find(a => (a.email || '').toLowerCase() === lc || (a.login_id || '').toLowerCase() === prefix) || null
}

// ─── Property picker ──────────────────────────────────────────────────────────
// coreScope: { required, agentId } — when required (role=agent), core results are scoped
// server-side to that agent's own listings; required with no bridged agentId searches
// nothing rather than everything (fail closed). Admin/manager pass required=false.
function DraftPicker({ onPick, coreScope }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const timer = useRef(null)

  useEffect(() => {
    clearTimeout(timer.current)
    if (q.trim().length < 3) { setResults([]); setErr(''); return }
    timer.current = setTimeout(async () => {
      setLoading(true); setErr('')
      // Old drafts DB (id/url/cadastral only) + new core DB (phone, listing id, address) run
      // in parallel and merge — each tagged with its source so pickDraft knows whether a
      // draft_id exists to attach the viewing to (see pickDraft below).
      const coreSearch = coreScope?.required && !coreScope.agentId
        ? Promise.resolve([])
        : searchCoreListings(q, { agentId: coreScope?.required ? coreScope.agentId : null })
      const [draftsR, coreR] = await Promise.allSettled([searchDrafts(q), coreSearch])
      const drafts = draftsR.status === 'fulfilled' ? draftsR.value.map(d => ({ ...d, __source: 'draft' })) : []
      const core   = coreR.status === 'fulfilled' ? coreR.value.map(d => ({ ...d, __source: 'core' })) : []
      if (draftsR.status === 'rejected' && coreR.status === 'rejected') setErr(draftsR.reason.message)
      setResults([...drafts, ...core])
      setLoading(false)
    }, 350)
    return () => clearTimeout(timer.current)
  }, [q, coreScope?.required, coreScope?.agentId])

  return (
    <div className="space-y-2">
      <label className={lbl}>{t('dv_search_label')}</label>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('dv_search_ph')} className={inp} />
      {loading && <div className="text-xs text-muted-foreground">{t('dv_searching')}</div>}
      {err && <div className="text-xs text-destructive">{err}</div>}
      {!loading && q.trim().length >= 3 && results.length === 0 && !err &&
        <div className="text-xs text-muted-foreground italic">{t('dv_no_results')}</div>}
      {results.length > 0 && (
        <div className="border border-border rounded-lg divide-y divide-border/50 max-h-64 overflow-y-auto">
          {results.map(d => (
            <button key={`${d.__source}-${d.id}`} onClick={() => onPick(d)} type="button"
              className="w-full text-left px-3 py-2 hover:bg-accent/40 transition-colors">
              <div className="flex items-center gap-2">
                <span className={`${COUNTER} shrink-0 ${d.__source === 'core' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' : 'bg-muted text-muted-foreground'}`}>
                  {d.__source === 'core' ? 'core' : 'draft'}
                </span>
                {d.short_code && <span className="text-xs font-mono text-muted-foreground">{d.short_code}</span>}
                <span className="text-sm truncate flex-1">{d.address || '—'}</span>
                <span className={`${COUNTER} bg-muted text-muted-foreground shrink-0`}>{dealTypeLabel(d.deal_type)}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {d.rooms_count ? `${d.rooms_count} ${t('dv_rooms')} · ` : ''}
                {d.apt_floor != null ? `${d.apt_floor}/${d.total_floors ?? '?'} ${t('dv_floor')} · ` : ''}
                {d.area_sq_m ? `${d.area_sq_m} m² · ` : ''}
                {fmtMoney(d.price, d.price_unit)}
                {d.owner_email ? ` · ${t('dv_owner_agent')}: ${d.owner_email.split('@')[0]}` : ''}
                {d.contact_name || d.contact_phone ? ` · ${[d.contact_name, d.contact_phone].filter(Boolean).join(' · ')}` : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Contact (owner / client): pick from the draft's contacts or type manually ─
function ContactBlock({ label, contacts, name, phone, onChange }) {
  return (
    <div className="space-y-1.5">
      <label className={lbl}>{label}</label>
      {contacts && contacts.length > 0 && (
        <select className={inp} value=""
          onChange={e => { const c = contacts[Number(e.target.value)]; if (c) onChange({ name: c.name || '', phone: c.phone || '' }) }}>
          <option value="">— {t('dv_manual')} —</option>
          {contacts.map((c, i) => <option key={i} value={i}>{c.name || '?'} · {c.phone || '—'} ({c.type})</option>)}
        </select>
      )}
      <div className="grid grid-cols-2 gap-2">
        <input className={inp} placeholder={t('dv_name')}  value={name  || ''} onChange={e => onChange({ name: e.target.value, phone })} />
        <input className={inp} placeholder={t('dv_phone')} value={phone || ''} onChange={e => onChange({ name, phone: e.target.value })} />
      </div>
    </div>
  )
}

// ─── Related deals — other records sharing this property / client / agent ──────
// The property↔client↔agent triangle, surfaced inside the modal: open a deal and see
// every other deal on the same property, with the same client (by phone), or handled by
// the same agent — filter pills switch dimension. Click one to jump to it.
function RelatedViewings({ currentId, propertyKey, propertyAddr, clientPhone, agentIds, allViewings, agents, onOpen }) {
  const dims = useMemo(() => {
    const others = (allViewings || []).filter(v => v.id !== currentId)
    const phone = normPhone(clientPhone)
    const ids = agentIds || []
    return {
      property: (propertyKey || propertyAddr)
        ? others.filter(v => propertyKey ? v.property_draft_id === propertyKey
                                         : (v.property_snapshot?.address && v.property_snapshot.address === propertyAddr))
        : [],
      client: phone ? others.filter(v => normPhone(v.client_phone) === phone) : [],
      agent:  ids.length ? others.filter(v => (v.agent_ids || []).some(id => ids.includes(id))) : [],
    }
  }, [allViewings, currentId, propertyKey, propertyAddr, clientPhone, agentIds])

  const order = ['property', 'client', 'agent']
  // up to 2 pills selected, kept in click-order; selecting a 3rd evicts the oldest. Default: none.
  const [sel, setSel] = useState([])
  if (dims.property.length + dims.client.length + dims.agent.length === 0) return null
  function clickPill(x) {
    setSel(cur =>
      cur.length === 2 ? (cur.includes(x) ? [x] : [cur[1], x])   // 2 sel: click a selected → keep it; click new → drop oldest
      : cur.length === 1 ? (cur[0] === x ? [] : [cur[0], x])     // 1 sel: same → off; other → add
      : [x])
  }
  // none → everything (union of all dims); one → that dim; two → intersection (AND)
  let list = []
  if (sel.length === 0) {
    const seen = new Set()
    for (const d of order) for (const v of dims[d]) if (!seen.has(v.id)) { seen.add(v.id); list.push(v) }
  } else if (sel.length === 1) {
    list = dims[sel[0]]
  } else {
    const inB = new Set(dims[sel[1]].map(v => v.id))
    list = dims[sel[0]].filter(v => inB.has(v.id))
  }

  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        <span className="text-xs font-medium text-muted-foreground mr-1">{t('dv_related')}</span>
        {order.map(d => (
          <button key={d} type="button" onClick={() => clickPill(d)} disabled={!dims[d].length}
            className={pill('none', { door: true, extra: `transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${sel.includes(d) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/40'}` })}>
            {t('dv_group_' + d)} {dims[d].length}
          </button>
        ))}
      </div>
      {list.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">{t('dv_none')}</div>
      ) : (
        <div className="rounded-lg border border-border divide-y divide-border/50 max-h-48 overflow-y-auto">
          {list.map(v => {
            const snap = v.property_snapshot || {}
            const names = (v.agent_ids || []).map(id => agents.find(a => a.id === id)?.name).filter(Boolean)
            return (
              <button key={v.id} type="button" onClick={() => onOpen(v)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent/30 text-left">
                <span className={`${COUNTER} shrink-0 ${STATUS_BADGE[v.status] || ''}`}>{t('dv_status_' + v.status)}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs truncate">{snap.address || '—'}</div>
                  <div className="text-xs text-muted-foreground truncate">{v.client_name || '—'}{names.length ? ` · ${names.join(', ')}` : ''}</div>
                </div>
                <span className="text-xs text-emerald-600 dark:text-emerald-400 shrink-0">{fmtMoney(v.commission_amount, v.commission_currency)}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Create / edit form ────────────────────────────────────────────────────────
// ⚠️ `agents` is the OFFICE-FILTERED roster — it is what the attendee picker offers, and
// filtering it there is correct. `allAgents` is the unfiltered one, and every COMMISSION
// SNAPSHOT must read from it. The two diverge whenever the header office filter is set (a
// manager or agent_hr is LOCKED to their own office, App.jsx:546, so for them they always
// diverge). A snapshot taken from the filtered list silently omits whoever is outside the
// current filter, and a snapshot is frozen: for manager_links there is no read-time
// fallback, so the override would be lost permanently rather than merely degraded.
// Defaults to `agents` so an older call site cannot crash — same list, previous behaviour.
export function ViewingForm({ agents, allAgents = null, viewing, allViewings, onClose, onSaved, onOpen, aiSeed, propertySeed }) {
  const backdropDismiss = useDeliberateBackdropDismiss(onClose)
  const roster = allAgents && allAgents.length ? allAgents : agents
  const isEdit = !!viewing
  const snap = viewing?.property_snapshot || {}
  const [draft, setDraft]       = useState(null)   // picked draft (create flow)
  const [contacts, setContacts] = useState([])
  const [property, setProperty] = useState(isEdit
    ? { address: snap.address, rooms_count: snap.rooms_count, apt_floor: snap.apt_floor, total_floors: snap.total_floors, area_sq_m: snap.area_sq_m, cadastral_code: viewing.cadastral_code, deal_type: viewing.deal_type, price: viewing.price, price_unit: viewing.price_unit, short_code: snap.short_code, source_url: snap.source_url, draft_id: viewing.property_draft_id }
    : null)
  // Core-search visibility scope: admin/general_manager search all core listings; manager/
  // agent_hr/agent only their own (server-side filter — see searchCoreListings). Role loads
  // async; until it resolves we treat the search as scoped-with-no-identity, i.e. core
  // returns nothing, never everything (fail closed — same principle as the server's
  // missing-party case). Error fallback defaults to 'manager' (restricted), the safer side.
  const session = useAuth()
  const [role, setRole] = useState(null)
  useEffect(() => { getMyRole().then(setRole).catch(() => setRole('manager')) }, [])
  const myAgentId = agents.find(a => a.auth_user_id && a.auth_user_id === session?.user?.id)?.id || null
  const coreScope = (role === 'admin' || role === 'general_manager')
    ? { required: false, agentId: null }
    : { required: true, agentId: myAgentId }
  // aiSeed only applies in create mode (isEdit=false) — an existing viewing's own values always win.
  const [client, setClient] = useState({ name: viewing?.client_name || aiSeed?.clientName || '', phone: viewing?.client_phone || aiSeed?.clientPhone || '' })
  const [owner,  setOwner]  = useState({ name: viewing?.owner_name  || '', phone: viewing?.owner_phone  || '' })
  const [agentIds, setAgentIds] = useState(viewing?.agent_ids || [])
  const [leadAgentId, setLeadAgentId] = useState(viewing?.lead_agent_id || '')
  const [comm, setComm] = useState({
    type:   viewing?.commission_type || 'percent',
    pct:    viewing?.price_pct ?? '',
    fixed:  viewing?.commission_type === 'fixed' ? (viewing?.commission_amount ?? '') : '',
    khelze: viewing?.commission_khelze ?? '',
    source: viewing?.commission_source || 'manual',
  })
  const [status, setStatus] = useState(viewing?.status || 'scheduled')
  // New viewings are scheduled at a precise date + time.
  // ⚠️ TWO FIXES, both 2026-08-15. (1) On an EXISTING meeting these seed the reschedule
  // drawer, and they were seeded from TODAY — the drawer opened showing today's date and a
  // blank time, never the appointment it was about to move, and saving wrote today at noon.
  // The core modal seeds from `tbilisiParts(scheduled_at)` and is the pattern here.
  // (2) `new Date().toISOString().slice(0,10)` is the UTC date, so between 00:00 and 04:00
  // Tbilisi the new-meeting form pre-filled YESTERDAY. tbilisiToday is the ruled-on helper.
  const seededWhen = tbilisiParts(
    viewing ? ((viewing.events || []).find(e => e.kind === 'scheduled' || e.kind === 'reschedule')?.event_at
      || viewing.scheduled_at || null) : null)
  const [schedDate, setSchedDate] = useState(aiSeed?.date || seededWhen.date || tbilisiParts(new Date().toISOString()).date)
  const [schedTime, setSchedTime] = useState(aiSeed?.time || seededWhen.time || '')
  // Deposit (ბე) is READ-ONLY here since the 2026-08-06 ruling (B340) — the meeting record
  // owns it. No setter: this is derived from the row, never edited, and useState with a
  // dead setter would invite someone to wire an input back in. useMemo keeps it a plain
  // projection of the row, which is all it is now.
  //
  // B340 — core is the binder's home; the legacy columns are only the seed for rows written
  // before that ruling. If core already holds a LIVE one for this property it WINS the
  // display, so the deal screen and the meeting can never quote different numbers at the
  // same agent. Display only — the write path lives on the meeting (see the panel below).
  const [coreBinder, setCoreBinder] = useState(null)
  useEffect(() => {
    // ⚠️ `property_snapshot.core_property_id`, and there is NO top-level column to fall back
    // to — measured against the scheduler DB 2026-08-06: `viewings` has 29 columns and
    // `core_property_id` is not one of them. It is written INSIDE the snapshot jsonb (see the
    // record builder below). An inherited `viewing?.core_property_id ||` prefix was removed
    // rather than kept as a harmless no-op: a lookup that names a column nobody has means the
    // next reader goes looking for it.
    const propId = viewing?.property_snapshot?.core_property_id
    if (!propId) return
    let dead = false
    listBinders({ propertyId: propId })
      .then(r => {
        if (dead) return
        const live = (r.binders || []).find(b => b.status === 'held') || null
        if (live) setCoreBinder(live)
      })
      .catch(() => { /* offline or none — the legacy seed still shows history */ })
    return () => { dead = true }
  }, [viewing?.id])  // eslint-disable-line react-hooks/exhaustive-deps
  const binder = useMemo(() => {
    const legacy = {
      amount: viewing?.binder_amount ?? '', currency: viewing?.binder_currency || 'GEL',
      paid: viewing?.binder_paid_date || '', close: viewing?.binder_close_date || '',
      holder: viewing?.binder_holder || 'agent',
    }
    if (!coreBinder) return legacy
    return {
      ...legacy,
      amount: coreBinder.amount ?? legacy.amount,
      currency: coreBinder.currency || legacy.currency,
      paid: coreBinder.taken_on || legacy.paid,
      close: coreBinder.deadline || legacy.close,
    }
  }, [viewing, coreBinder])
  const [paymentDate, setPaymentDate] = useState(viewing?.payment_date || '')
  const [notes, setNotes] = useState(viewing?.notes || aiSeed?.notes || '')
  const [saving, setSaving] = useState(false)
  // The one thing this form never had: somewhere to say a write failed. See the catch in
  // save() for what its absence cost.
  const [err, setErr] = useState('')
  const [localEvents, setLocalEvents] = useState(viewing?.events || [])  // timeline, updated live
  const [rescheduling, setRescheduling] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [undo, setUndo] = useState(null)   // { prevStatus, eventId, kind } — 15s revert window
  const [undoSecs, setUndoSecs] = useState(0)

  // Current commission % per agent, snapshotted onto the deal when it closes.
  const commByAgent = () => Object.fromEntries(roster.map(a => [a.id, a.commission]))
  // …and the office managers owed a team override on it, resolved from the SAME live agent
  // list at the SAME instant and frozen alongside. `agents` here is every agent, not the
  // attendees — the managers we are looking for are by definition not in the meeting.

  // Record an outcome: log a timestamped event + advance status live (binder/payment sections follow)
  async function doOutcome(kind, newStatus, when) {
    const at = when || new Date()
    if (isNaN(at.getTime())) { setErr(t('dv_bad_date')); return }
    // ⚠️ CLOSING IS NOT A LABEL CHANGE, so it asks first (2026-08-15). One tap used to end
    // the cooperation, drive the listing to sold/rented_out and award ranking credit — and
    // the Undo beside it reverts only the status, none of that. A control whose effects
    // outlive its undo has to say so before it acts, not after.
    if (newStatus === 'closed' && !window.confirm(t('dv_close_confirm'))) return
    // event_date is a GEORGIAN day (owner ruling 2026-08-12). `toISOString().slice(0,10)` is
    // the UTC date, so between 00:00 and 04:00 Tbilisi every outcome was filed to yesterday.
    const ev = { kind, event_at: at.toISOString(), event_date: tbilisiParts(at.toISOString()).date, new_status: newStatus, created_by: getCurrentAdmin() }
    const prevStatus = status
    try {
    if (isEdit) {
      const saved = await addViewingEvent(viewing.id, ev)
      setLocalEvents(list => [saved, ...list])
      setUndo({ prevStatus, eventId: saved.id, kind })   // arm the 15s revert
      // Lock in each agent's commission % at the moment of close
      if (newStatus === 'closed') {
        await snapshotViewingCommissions(viewing.id, commByAgent(),
          managerLinksForDeal(agentIds, roster, await stampOpts(ev.event_date)))
        // Core deal closure (0086): sold/rented lifecycle on the listing + end the
        // cooperation with reason deal_closed + deal.closed event per attending agent
        // (ranking points). Only possible when the meeting was picked from core —
        // property_snapshot.core_listing_id is the durable ref. Best-effort: a core
        // hiccup must never block recording the close itself.
        const coreListingId = viewing.property_snapshot?.core_listing_id
        if (coreListingId && agentIds.length) {
          // girao/ijara close as rented_out, NOT sold (0219). record_deal_closure only
          // accepts sold|rented_out, and the else-branch would have filed them as sold —
          // marking the property permanently gone. A girao ends: the sum is returned and
          // the flat comes back to market after N years, which is the rented_out
          // lifecycle exactly. Listed explicitly so a new deal type can never again be
          // absorbed by the default and mean "sold" by accident.
          // B118 (2026-07-29): when deal_type is NULL we used to fall through to 'sold' —
          // a guess, and since 0180 propagates the terminal state to listing.status a
          // wrong guess marks a rental permanently gone. Send NO outcome instead: the
          // server derives it from listing.intent (always set at ingest), which knows
          // the answer rather than guessing it.
          const dt = viewing.deal_type || property?.deal_type
          const outcome = dt ? (['lease', 'daily_rent', 'girao', 'ijara'].includes(dt) ? 'rented_out' : 'sold') : undefined
          // ⚠️ THE STORED NUMBER, not the unsaved form (fixed 2026-08-15). This sent the
          // form's live `commAmount` — which nothing has written yet, because doOutcome does
          // not save — so core was told a commission the scheduler database did not hold,
          // and the two disagreed permanently with nothing to reconcile them. For an
          // unedited form the two are equal by construction; when they differ, the record
          // is the truth and the agent's unsaved edit is not.
          reportDealClosure(coreListingId, {
            outcome, scheduler_agent_ids: agentIds, viewing_id: viewing.id,
            commission_amount: viewing.commission_amount ?? commAmount ?? null,
            commission_currency: viewing.commission_currency || commCurrency,
          }).catch(e => console.error('[deal-closed→core]', e.message))
        }
      }
      onSaved()
    }
    setStatus(newStatus)
    } catch (e) { setErr(e?.message || String(e)) }
  }
  // 15-second window to revert the last outcome (like candidate→agent hire revert)
  useEffect(() => {
    if (!undo) return
    setUndoSecs(15)
    const iv = setInterval(() => setUndoSecs(s => Math.max(0, s - 1)), 1000)
    const tm = setTimeout(() => setUndo(null), 15000)
    return () => { clearInterval(iv); clearTimeout(tm) }
  }, [undo])
  async function revertOutcome() {
    if (!undo) return
    const u = undo; setUndo(null)
    try {
      await deleteViewingEvent(u.eventId)
      await updateViewing(viewing.id, { status: u.prevStatus })
      setLocalEvents(list => list.filter(e => e.id !== u.eventId))
      setStatus(u.prevStatus)
      onSaved()
    } catch (e) { setErr(e?.message || String(e)) }
  }
  async function addNote() {
    const txt = noteText.trim(); if (!txt) return
    const at = new Date()
    try {
      // The row the SERVER returns, not a hand-built {id:'tmp-…'} — the local stand-in has
      // no real id, so the timeline could not delete or re-render it afterwards.
      const saved = await addViewingEvent(viewing.id, { kind: 'note', event_at: at.toISOString(), event_date: tbilisiParts(at.toISOString()).date, note: txt, created_by: getCurrentAdmin() })
      setLocalEvents(list => [saved || { id: 'tmp-' + at.getTime(), kind: 'note', event_at: at.toISOString(), note: txt }, ...list])
      setNoteText('')
    } catch (e) { setErr(e?.message || String(e)) }
  }

  function pickDraft(d) {
    // A core-DB pick (see DraftPicker) is NOT a row in the old drafts table — its id must
    // never land in property_draft_id (that FK points at the old project). null it here so
    // every downstream read of draft?.id (record.property_draft_id, RelatedViewings' key)
    // stays null for these, same as a hand-typed property with no draft at all.
    const fromCore = d.__source === 'core'
    // Core picks may carry the property's RANKED contact list (contact resolution chain —
    // see getPropertyContacts): the default is pre-selected below, the rest live in
    // ContactBlock's dropdown as overrides. Agent-authored parties (their own reshare)
    // are labeled and never chosen as the default owner contact.
    const contacts = fromCore
      ? (Array.isArray(d.contacts_ranked) && d.contacts_ranked.length
          ? d.contacts_ranked.map(c => ({
              name: c.name, phone: c.phone,
              type: c.is_agent ? 'agent' : (c.role || c.evidence || 'contact'),
              __default: !!d.default_contact_party_id && c.party_id === d.default_contact_party_id,
              __agent: !!c.is_agent, agentId: c.agent_id || null,
            }))
          : (d.contact_name || d.contact_phone ? [{ name: d.contact_name, phone: d.contact_phone, type: 'owner' }] : []))
      : (d.contacts || [])
    // Core join keys, snapshotted on save. core_property_id is the PRIMARY relationship: a
    // meeting is about showing a PROPERTY, which persists; core_listing_id is an optional
    // secondary ref to the ONE ad the pick came from (listings come and go). Before core_listing_id
    // a core pick kept only source_url, and hand-cleared URLs left the viewing unresolvable
    // (measured 2026-07-04: 0/118). Now property is the durable anchor even when every ad expires.
    // d.id = the picked listing's uuid (may be null for a property-primary pick); d.property_id
    // = its property. Meeting-from-a-property-card passes property_id with a null listing id.
    setDraft(fromCore ? { ...d, id: null, core_listing_id: d.id || null, core_property_id: d.property_id || null } : d)
    setContacts(contacts)
    setProperty({ address: d.address, rooms_count: d.rooms_count, apt_floor: d.apt_floor, total_floors: d.total_floors, area_sq_m: d.area_sq_m, cadastral_code: d.cadastral_code, deal_type: d.deal_type, price: d.price, price_unit: d.price_unit, short_code: d.short_code, source_url: d.source_url, draft_id: fromCore ? null : d.id })
    const c = resolveCommission(d)
    // Agreed cooperation terms (0046) outrank anything imported from the ad — the agency
    // already settled commission with this listing's contact. Shape-aware (2026-07-12):
    // the cooperation's commission_type maps onto this form's own modes —
    //   percent → percent | net_commission / fixed_net (flat fee) → fixed |
    //   net_amount (owner takes X, we keep the spread) → khelze (same semantics; trade-only
    //   in this form, same as the type is sale-only in the cooperation) |
    //   standard (rent: one month per 12-month lease) → percent 100 of the monthly rent —
    //   the canonical value; a shorter lease is a manual pro-rate at close time.
    const coop = fromCore ? d.cooperation : null
    const coopComm = (() => {
      if (!coop) return null
      const ct = coop.commission_type || (coop.commission_percent != null ? 'percent' : null)
      if (ct === 'percent' && coop.commission_percent != null)
        return { type: 'percent', pct: Number(coop.commission_percent), fixed: '', khelze: '' }
      if ((ct === 'net_commission' || ct === 'fixed_net') && coop.net_commission_amount != null)
        return { type: 'fixed', pct: '', fixed: Number(coop.net_commission_amount), khelze: '' }
      if (ct === 'net_amount' && coop.owner_net_amount != null)
        return { type: 'khelze', pct: '', fixed: '', khelze: Number(coop.owner_net_amount) }
      if (ct === 'standard') {
        // ⚠️ RENT ONLY (fixed 2026-08-15). "standard" is the sentence "one month's rent per
        // 12-month lease", so 100% OF THE MONTHLY RENT is its canonical value — see the
        // mapping note above. That sentence says nothing about a sale, and this branch was
        // intent-blind: a `standard` cooperation on a sale ad resolved to 100% OF THE SALE
        // PRICE and was labelled "from cooperation", i.e. the agency claiming the whole
        // building. With no intent on the seed we cannot tell, and an unresolved commission
        // the agent types in beats a confident wrong one.
        const rentish = coop.transaction_intent === 'rent'
        return rentish ? { type: 'percent', pct: 100, fixed: '', khelze: '' } : null
      }
      return null
    })()
    if (coopComm) {
      setComm({ ...coopComm, source: 'cooperation' })
    } else {
      setComm({ type: c.type, pct: c.pct, fixed: c.fixed, khelze: c.khelze, source: c.source })
    }
    // pre-fill owner contact: the resolver's default first, then an 'owner'-type contact,
    // then any NON-agent contact — never our own agent's number as "owner" (blank beats wrong).
    const ownerC = contacts.find(x => x.__default) || contacts.find(x => x.type === 'owner') || contacts.find(x => !x.__agent) || null
    if (ownerC) setOwner({ name: ownerC.name || '', phone: ownerC.phone || '' })
    const clientC = contacts.find(x => x.type === 'client')
    if (clientC) setClient({ name: clientC.name || '', phone: clientC.phone || '' })
    // Old-drafts auto-link: owner_email → agents.login_id prefix match.
    // Core auto-link: a listing author who resolved to a bridged scheduler agent (0040
    // resolve_agent_party) — the SAME signal the contact resolver sinks to rank 9 (never
    // the owner contact) is repurposed here as "this agent handles the property" (a
    // different question). c.agent_id carries agents.id directly, no extra lookup needed.
    const ag = fromCore
      ? (contacts.find(c => c.__agent && c.agentId) ? { id: contacts.find(c => c.__agent && c.agentId).agentId } : null)
      : matchOwnerAgent(agents, d.owner_email)
    // Dedup INSIDE the updater: pickDraft can run twice with the same stale agentIds closure
    // (StrictMode double-firing the propertySeed mount effect) — checking outside duplicated
    // the auto-picked agent chip.
    if (ag && agents.some(a => a.id === ag.id)) setAgentIds(ids => ids.includes(ag.id) ? ids : [...ids, ag.id])
  }

  function toggleAgent(id) {
    setAgentIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id])
  }

  // propertySeed = a core-pick-shaped object handed in by a caller that already knows the
  // property (the Desk listing modal's "Add meeting") — same path as picking it in the
  // DraftPicker, just skipping the search. Create mode only; runs once on mount.
  useEffect(() => {
    if (!isEdit && propertySeed && !property) pickDraft(propertySeed)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // Commission resolves to one number in the deal currency; the % base is the deal price
  // (lease = monthly rent, trade = sale price), so correcting the price recomputes it.
  // ⚠️ THE COOPERATION'S CURRENCY WINS WHEN THE COMMISSION CAME FROM IT (2026-08-15). This
  // read the AD's price unit unconditionally, so a fee agreed in USD on a GEL-priced listing
  // was stored as GEL — the number kept, the unit swapped. The ad's unit is still the right
  // default when the figure is the agent's own, because then the base IS the ad's price.
  const commCurrency = (comm.source === 'cooperation' && draft?.cooperation?.min_price_currency)
    || property?.price_unit || 'GEL'
  const commAmount = property ? computeCommission(comm.type, property.price, comm.pct, comm.fixed, comm.khelze) : 0
  const splitEach = commAmount && agentIds.length ? Math.round(commAmount / agentIds.length) : null

  async function save() {
    setSaving(true)
    try {
      const record = {
        property_draft_id: isEdit ? viewing.property_draft_id : (draft?.id || null),
        cadastral_code:    property?.cadastral_code || null,
        deal_type:         property?.deal_type || null,
        // ⚠️ MERGED OVER THE STORED SNAPSHOT, NOT REBUILT FROM TEN KEYS (fixed 2026-08-15).
        // This jsonb is written by several producers — the backfill put `district_code` in
        // it, and this file's own search reads that key — but Save rebuilt the object from
        // the fields the FORM happens to know, so every other key was erased the first time
        // anyone edited a note. A meeting silently dropped out of district search by being
        // saved. Spread first, then overwrite what the form actually owns.
        property_snapshot: {
          ...snap,
          address: property?.address, rooms_count: property?.rooms_count, apt_floor: property?.apt_floor,
          total_floors: property?.total_floors, area_sq_m: property?.area_sq_m,
          source_url: draft?.source_url || snap.source_url, short_code: draft?.short_code || snap.short_code,
          owner_agent_email: draft?.owner_email || snap.owner_agent_email,
          core_listing_id: draft?.core_listing_id || snap.core_listing_id || null,
          core_property_id: draft?.core_property_id || snap.core_property_id || null,
        },
        client_name: client.name || null, client_phone: client.phone || null,
        owner_name:  owner.name  || null, owner_phone:  owner.phone  || null,
        lead_agent_id: agentIds.length > 1 && agentIds.includes(leadAgentId) ? leadAgentId : null,
        price: property?.price != null && property?.price !== '' ? Number(property.price) : null,
        price_unit: property?.price_unit || null,
        price_pct: comm.type === 'percent' && comm.pct !== '' ? Number(comm.pct) : null,
        commission_type: comm.type,
        commission_khelze: comm.type === 'khelze' && comm.khelze !== '' ? Number(comm.khelze) : null,
        commission_amount: commAmount || null,
        commission_currency: commCurrency,
        commission_source: comm.source,
        payment_date: status === 'closed' ? (paymentDate || null) : null,
        // ⚠️ DEPOSIT (ბე) IS NOT WRITTEN HERE ANY MORE — owner ruling 2026-08-06, B340.
        // The core meeting record is the single source of truth for deposits. These columns
        // were a second home for the same business fact, and only the CORE one is watched by
        // the expiry nudge — so a deposit recorded here used to run out with nobody warned.
        //
        // The legacy columns are deliberately NOT sent (not even as null): an UPDATE that
        // writes null would erase history on any row that still holds a figure, and "keep
        // audit history" is part of the ruling. Omitting the keys leaves whatever is stored
        // untouched, which is why the read path below still displays them.
        //
        // Audited before this change (service-role, on the box): of 189 `viewings` rows,
        // binder_amount was non-null on ZERO. One stray binder_currency and one binder_holder
        // exist with no amount. So there was nothing to migrate — do not go looking for a
        // backfill that the audit already proved empty.
        //
        // What stood here, recorded because the failure mode is worth remembering:
        //     binder_amount: status === 'binder' && … ? Number(binder.amount) : null,
        //     …and the same `status === 'binder' ? … : null` on currency, paid, close, holder
        // inside a FULL-RECORD update — so moving a deal from `binder` to `closed` wrote
        // NULL over all five. The money did not survive the outcome it was taken for, and
        // nobody would have seen it happen: the deal looked successful and the deposit
        // record simply ceased to exist.
        //
        // Nor are they re-written with preserved values. Two homes for one business fact is
        // the disease (B340); keeping this one warm "just in case" is how a system ends up
        // with two numbers and no way to tell which is true.
        status, notes: notes || null,
      }
      // Snapshot commission rates only when the deal is (being) closed.
      // NOTE: do NOT name this `comm` — the commission form state is `comm` and is read
      // above when building `record`; a block-scoped `const comm` here shadowed it and threw
      // a TDZ ReferenceError on every save (swallowed by the catch-less try). Keep it distinct.
      // ⚠️ ONLY WHEN THE DEAL IS BEING CLOSED *NOW* (fixed 2026-08-15). This ran on EVERY
      // save of an already-closed deal, so editing a note on a deal closed months ago
      // overwrote each agent's stored `commission_pct` with TODAY's rate — defeating the
      // one thing the snapshot exists for ("income reflects the rate the agent had at
      // close"), silently, with nothing on screen showing the value or that it would move.
      const closingNow = status === 'closed' && (!isEdit || viewing?.status !== 'closed')
      const commSnapshot = closingNow ? commByAgent() : null
      // THE SECOND CLOSE PATH. doOutcome()'s ✓ button is not the only way a deal reaches
      // 'closed' — the status chips in this form do it too, and a deal can even be CREATED
      // closed. commission_pct was already snapshotted here (commSnapshot); manager_links
      // has to be, or an override silently depends on which control the agent happened to
      // use. It rides on `record` because manager_links is a column on viewings itself.
      // The close date is the PAYMENT date when one is given (that is what viewingCloseDate
      // derives a deal's close from); with none, the deal is closing today.
      if (closingNow) record.manager_links = managerLinksForDeal(agentIds, roster,
        await stampOpts(paymentDate || tbilisiParts(new Date().toISOString()).date))
      // ⚠️ This screen deliberately has NO binder write path — not even a pass-through to
      // core's gateway. The branch that built one is merged here with it removed, because a
      // second door for the same figure is what B340 ruled against, and the door it opened
      // could not satisfy the gateway anyway: `amend_binder` demands a reason for a
      // confirmed amount (0446) and there is nowhere here to ask for one.
      //
      // ⚠️ One real case is left uncovered and is filed as B367: a LEGACY deal with no core
      // meeting now has no surface at all that can record a deposit. Do not close that gap
      // by re-adding a form here — the fix belongs where the question can be asked.
      if (isEdit) {
        await updateViewing(viewing.id, record, agentIds, commSnapshot)
        onSaved()
      } else {
        record.created_by = getCurrentAdmin()
        // Tbilisi wall clock, never the device's (owner ruling 2026-08-12)
        const evAt = composeTbilisi(schedDate, schedTime)
        const saved = await addViewing(record, agentIds, { kind: 'scheduled', event_date: schedDate, event_at: evAt, new_status: status, note: notes || null, created_by: getCurrentAdmin() }, commSnapshot)
        onSaved(saved)
      }
    } catch (e) {
      // ⚠️ THIS FORM HAD NO CATCH AT ALL until 2026-08-15 — `try { … } finally { … }`. A
      // failed save put the button back to "Save" and said nothing, so a lost edit and a
      // saved one were the same screen. (It also swallowed a live TDZ crash, which is
      // recorded in the comment above `commSnapshot`.) The core modal has carried an error
      // line since it was built; the surface holding 67% of the meetings had none.
      setErr(e?.message || String(e))
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      {...backdropDismiss}>
      <div className="bg-card border border-border rounded-xl w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0 bg-card rounded-t-xl">
          <div className="font-semibold text-sm">{isEdit ? property?.address || t('dv_property') : t('dv_new')}</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">&times;</button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
          {!isEdit && !property && aiSeed?.propertyHint && (
            <div className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 rounded-lg px-3 py-2">
              🤖 AI read this property from the message: <span className="font-medium">{aiSeed.propertyHint}</span> — search below to match it.
            </div>
          )}
          {!isEdit && !property && <DraftPicker onPick={pickDraft} coreScope={coreScope} />}
          {!isEdit && !property && <div className="text-xs text-muted-foreground italic">{t('dv_pick_first')}</div>}

          {property && (<>
            {/* Property snapshot + draft link */}
            <div className="bg-muted/30 rounded-lg p-3 text-sm flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium">{property.address || '—'}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {dealTypeLabel(property.deal_type)} · {fmtMoney(property.price, property.price_unit)}
                  {property.rooms_count ? ` · ${property.rooms_count} ${t('dv_rooms')}` : ''}
                  {property.apt_floor != null ? ` · ${property.apt_floor}/${property.total_floors ?? '?'} ${t('dv_floor')}` : ''}
                  {property.area_sq_m ? ` · ${property.area_sq_m} m²` : ''}
                  {property.cadastral_code ? ` · ${property.cadastral_code}` : ''}
                </div>
              </div>
              {(property.short_code || property.draft_id || property.source_url) && (
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {(property.short_code || property.draft_id) && (
                    <a href={`https://sentavus.com/drafts/${property.short_code || property.draft_id}`} target="_blank" rel="noreferrer"
                      className="text-xs font-medium text-primary hover:underline whitespace-nowrap">{t('dv_open_draft')} ↗</a>
                  )}
                  {property.source_url && (
                    <a href={property.source_url} target="_blank" rel="noreferrer"
                      className="text-xs text-muted-foreground hover:text-foreground hover:underline whitespace-nowrap">{t('dv_listing')} ↗</a>
                  )}
                </div>
              )}
            </div>

            <ContactBlock label={t('dv_owner')}  contacts={contacts} name={owner.name}  phone={owner.phone}  onChange={setOwner} />
            <ContactBlock label={t('dv_client')} contacts={contacts} name={client.name} phone={client.phone} onChange={setClient} />

            {/* Agents — picked agent(s) as chips + dropdown to add another active agent */}
            <div>
              <label className={lbl}>{t('dv_agents')}</label>
              {!isEdit && aiSeed?.agentHint && agentIds.length === 0 && (
                <div className="text-xs text-amber-700 dark:text-amber-400 mb-1"><Icon name="bot" /> AI read agent: <span className="font-medium">{aiSeed.agentHint}</span> — pick them below.</div>
              )}
              <div className="flex flex-wrap items-center gap-1.5">
                {agentIds.map(id => {
                  const a = agents.find(x => x.id === id)
                  return (
                    <span key={id} className={`${COUNTER} bg-primary text-primary-foreground`}>
                      {a?.name || '—'}
                      <button type="button" onClick={() => toggleAgent(id)} className="hover:opacity-70 leading-none text-sm">&times;</button>
                    </span>
                  )
                })}
                <select className={pill('none', { door: true, extra: 'border border-dashed border-border bg-background text-muted-foreground hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-ring' })}
                  value="" onChange={e => { if (e.target.value) toggleAgent(e.target.value) }}>
                  <option value="">+ {t('dv_add_agent')}</option>
                  {agents.filter(a => a.is_active !== false && !agentIds.includes(a.id))
                    .map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              {/* Who attended the meeting — drives the commission split when 2 agents */}
              {agentIds.length > 1 && (
                <div className="mt-2">
                  <label className={lbl}>{t('dv_lead_agent')}</label>
                  <select className={inp} value={leadAgentId} onChange={e => setLeadAgentId(e.target.value)}>
                    <option value="">{t('dv_lead_default')}</option>
                    {agentIds.map(id => {
                      const a = agents.find(x => x.id === id)
                      return <option key={id} value={id}>{a?.name || '—'}</option>
                    })}
                  </select>
                </div>
              )}
            </div>

            {/* Commission — type-aware (percent / fixed / khelze). Currency follows the deal price;
                correcting the price (e.g. it sold lower) recomputes the % and khelze amounts. */}
            <div className="space-y-2">
              <label className={lbl}>{t('dv_commission')}</label>

              {/* Deal price — the % base (lease=monthly rent, trade=sale price) and khelze base */}
              <div className="grid grid-cols-3 gap-2">
                <input type="number" className={inp + ' col-span-2'} value={property.price ?? ''} placeholder={t('dv_deal_price')}
                  onChange={e => setProperty(p => ({ ...p, price: e.target.value }))} />
                <select className={inp} value={property.price_unit || 'GEL'}
                  onChange={e => setProperty(p => ({ ...p, price_unit: e.target.value }))}>
                  <option>GEL</option><option>USD</option>
                </select>
              </div>

              {/* Type — khelze only applies to a sale */}
              <div className="flex flex-wrap gap-1.5">
                {['percent', 'fixed', ...(property.deal_type === 'trade' ? ['khelze'] : [])].map(ty => (
                  <button key={ty} type="button" onClick={() => setComm(c => ({ ...c, type: ty, source: 'manual' }))}
                    className={pill('none', { door: true, extra: `transition-colors ${comm.type === ty ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/40'}` })}>
                    {t('dv_comm_' + ty)}
                  </button>
                ))}
              </div>

              {/* Type-specific input */}
              {comm.type === 'percent' && (
                <input type="number" className={inp} value={comm.pct} placeholder="%"
                  onChange={e => setComm(c => ({ ...c, pct: e.target.value, source: 'manual' }))} />
              )}
              {comm.type === 'fixed' && (
                <input type="number" className={inp} value={comm.fixed} placeholder={t('dv_comm_fixed')}
                  onChange={e => setComm(c => ({ ...c, fixed: e.target.value, source: 'manual' }))} />
              )}
              {comm.type === 'khelze' && (
                <input type="number" className={inp} value={comm.khelze} placeholder={t('dv_comm_khelze')}
                  onChange={e => setComm(c => ({ ...c, khelze: e.target.value, source: 'manual' }))} />
              )}

              {/* Resolved commission + source + equal split */}
              <div className="text-sm">
                <span className="font-medium text-emerald-600 dark:text-emerald-400">{fmtMoney(commAmount, commCurrency)}</span>
                <span className="text-xs text-muted-foreground ml-1">({t('dv_comm_' + comm.source)})</span>
                {splitEach != null && agentIds.length > 1 &&
                  <span className="text-xs text-muted-foreground ml-2">· {fmtMoney(splitEach, commCurrency)} {t('dv_split_each')} ({agentIds.length})</span>}
              </div>
            </div>

            {/* Scheduled for (new viewings) — exact appointment date + time */}
            {!isEdit && (
              <div className="grid grid-cols-2 gap-2">
                <div><label className={lbl}>{t('dv_sched_date')}</label><DateInput value={schedDate} onChange={setSchedDate} /></div>
                <div><label className={lbl}>{t('dv_sched_time')}</label><TimeInput className={inp} value={schedTime} onChange={setSchedTime} /></div>
              </div>
            )}

            {/* Outcome (edit): current status + context-aware "what happened" actions */}
            {isEdit ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">{t('dv_status')}:</span>
                  <span className={`${COUNTER} font-medium ${STATUS_BADGE[status] || ''}`}>{t('dv_status_' + status)}</span>
                </div>
                {(QUICK[status] || []).length > 0 && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">{t('dv_what_happened')}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(QUICK[status] || []).map(([k, ns]) => (
                        <button key={k} type="button" disabled={saving}
                          onClick={() => k === 'reschedule' ? setRescheduling(r => !r) : doOutcome(k, ns)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 ${ns === 'failed' ? 'border-red-300 dark:border-red-800 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20' : ns === 'closed' ? 'border-emerald-300 dark:border-emerald-800 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20' : ns === 'binder' ? 'border-violet-300 dark:border-violet-800 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20' : 'border-border text-foreground hover:bg-accent'}`}>
                          {t('dv_kind_' + k)}
                        </button>
                      ))}
                    </div>
                    {rescheduling && (
                      <div className="flex flex-wrap items-end gap-2 mt-2 bg-muted/30 rounded-lg p-2">
                        <div><label className={lbl}>{t('dv_sched_date')}</label><DateInput value={schedDate} onChange={setSchedDate} /></div>
                        <div><label className={lbl}>{t('dv_sched_time')}</label><TimeInput className={inp} value={schedTime} onChange={setSchedTime} /></div>
                        {/* ⚠️ A CLEARED DATE IS NOT A DATE (fixed 2026-08-15). DateInput's own
                            "Clear" commits '', composeTbilisi returns null for a falsy date,
                            and `new Date(null)` is the EPOCH — so pressing Clear and then Save
                            silently rescheduled the meeting to 1970-01-01, written immediately,
                            with no validation and no error. The button now refuses instead. */}
                        <button type="button" disabled={!schedDate}
                          onClick={() => {
                            const iso = composeTbilisi(schedDate, schedTime)
                            if (!iso) { setErr(t('dv_bad_date')); return }
                            doOutcome('reschedule', 'scheduled', new Date(iso)); setRescheduling(false)
                          }}
                          className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">{t('dv_save')}</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className={lbl}>{t('dv_status')}</label>
                <div className="flex flex-wrap gap-1.5">
                  {SETTABLE_STATUSES.map(s => (
                    <button key={s} type="button" onClick={() => setStatus(s)}
                      className={pill('none', { door: true, extra: `font-medium transition-colors ${status === s ? STATUS_BADGE[s] : 'bg-muted/50 text-muted-foreground hover:text-foreground'}` })}>
                      {t('dv_status_' + s)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Binder (ბე) — READ-ONLY compatibility layer. Owner ruling 2026-08-06 (B340):
                the meeting record is the single source of truth for deposits, and this screen
                became a display surface. Inputs are gone rather than disabled: a disabled field
                still reads as "this is where it goes, but not for you", which is the opposite
                of the message. A stored figure is shown so old rows stay legible; the panel
                does not render at all when there is nothing stored, so the common case is that
                agents simply never see a deposit box here again.
                ⚠️ Do not re-add inputs. If a deposit must be recorded, it belongs on the
                meeting — that is the only record the expiry warning can see. */}
            {status === 'binder' && (binder.amount !== '' || binder.paid || binder.close) && (
              <div className="rounded-lg p-3 bg-violet-50/40 dark:bg-violet-900/10 border border-violet-200/60 dark:border-violet-800/40">
                <div className="text-xs font-medium mb-1.5 opacity-70">{t('dv_binder_legacy_title')}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                  {binder.amount !== '' && (
                    <div><span className={lbl}>{t('dv_binder_amount')}</span>
                      <div className="tabular-nums">{binder.amount} {binder.currency}</div></div>
                  )}
                  {binder.paid && (
                    <div><span className={lbl}>{t('dv_binder_paid')}</span><div>{binder.paid}</div></div>
                  )}
                  {binder.close && (
                    <div><span className={lbl}>{t('dv_binder_close')}</span><div>{binder.close}</div></div>
                  )}
                </div>
                <div className="text-xs mt-2 opacity-70">{t('dv_binder_moved_hint')}</div>
              </div>
            )}

            {/* Commission payment date — when closed */}
            {status === 'closed' && (
              <div><label className={lbl}>{t('dv_payment_date')}</label>
                <DateInput value={paymentDate} onChange={setPaymentDate} /></div>
            )}

            <div><label className={lbl}>{t('dv_notes')}</label>
              <textarea rows={2} className={inp + ' resize-none'} value={notes} onChange={e => setNotes(e.target.value)} /></div>

            {isEdit && (
              <div className="border-t border-border pt-4 space-y-2">
                <div className="font-semibold text-sm">{t('dv_timeline')}</div>
                {localEvents.length > 0 && (
                  <div className="space-y-1.5">
                    {localEvents.map(e => (
                      <div key={e.id} className="flex items-start gap-2 text-xs">
                        <span className="text-muted-foreground shrink-0 w-24">{fmtDateTime(e)}</span>
                        <span className={`${COUNTER} bg-muted text-muted-foreground shrink-0`}>{t('dv_kind_' + e.kind)}</span>
                        <span className="flex-1">{e.note}{e.new_status ? ` → ${t('dv_status_' + e.new_status)}` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input className={inp} placeholder={t('dv_add_note')} value={noteText}
                    onChange={e => setNoteText(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNote()} />
                  <button type="button" onClick={addNote} disabled={!noteText.trim()}
                    className="px-3 py-2 rounded-lg border border-border text-sm hover:bg-accent disabled:opacity-50 shrink-0">+</button>
                </div>
              </div>
            )}

            <RelatedViewings
              currentId={viewing?.id}
              propertyKey={draft?.id || viewing?.property_draft_id}
              propertyAddr={property?.address}
              clientPhone={client.phone}
              agentIds={agentIds}
              allViewings={allViewings}
              agents={agents}
              onOpen={onOpen} />
          </>)}
        </div>

        {/* The error line this form never had. Above the footer, where the buttons that can
            fail are — a failed write used to leave the button reading "Save" and say nothing. */}
        {err && <p className="px-5 pb-2 text-xs text-destructive">{err}</p>}
        <div className="flex justify-between gap-2 px-5 py-4 border-t border-border shrink-0 bg-card rounded-b-xl">
          <div>
            {/* ⚠️ NAMES WHAT IS BEING ERASED, and reports a failure (2026-08-15). This asked
                literally "Delete?" — no address, no client, no date — and cascaded the
                timeline and the agent links on a bare confirm, with no catch. The core path
                is admin-only for the same act, on the ruling that erasing a business record
                is a narrower power than editing one; this one is left open to whoever can
                see the deal, but it must at least say what it is about to destroy. */}
            {isEdit && <button onClick={async () => {
              const what = [property?.address, client.name, fmtDate(schedDate)].filter(Boolean).join(' · ')
              if (!window.confirm(tLog('dv_delete_confirm', { what: what || t('dv_property') }))) return
              try { await deleteViewing(viewing.id); onSaved() } catch (e) { setErr(e?.message || String(e)) }
            }}
              className="text-sm text-destructive hover:underline">{t('dv_delete')}</button>}
          </div>
          <div className="flex items-center gap-2">
            {/* 15s revert window after an outcome action — same control as the agents modal */}
            {undo && (
              <button onClick={undoSecs > 0 ? revertOutcome : undefined} disabled={undoSecs === 0}
                title={undoSecs > 0 ? `${t('dv_undo')} (${undoSecs}s)` : ''}
                className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all shrink-0 relative ${undoSecs > 0 ? 'border-amber-400 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 cursor-pointer' : 'border-muted text-muted-foreground/40 cursor-default'}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
                {undoSecs > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 text-[9px] bg-amber-400 text-white rounded-full w-4 h-4 flex items-center justify-center font-bold tabular-nums">{undoSecs}s</span>
                )}
              </button>
            )}
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm hover:bg-accent">{t('dv_cancel')}</button>
            <button onClick={save} disabled={saving || !property} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
              {saving ? t('dv_saving') : t('dv_save')}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── List row ──────────────────────────────────────────────────────────────────
function ViewingRow({ viewing, agents, onClick }) {
  const snap = viewing.property_snapshot || {}
  const names = (viewing.agent_ids || []).map(id => agents.find(a => a.id === id)?.name).filter(Boolean)
  // current scheduled time = most recent scheduled/reschedule event (events are newest-first)
  const sched = (viewing.events || []).find(e => e.kind === 'scheduled' || e.kind === 'reschedule')
  const overdue = viewing.status === 'scheduled' && sched?.event_at && new Date(sched.event_at) < new Date()
  return (
    <button onClick={onClick} className="w-full flex items-center gap-3 px-4 py-3 border-b border-border/50 last:border-0 hover:bg-accent/30 transition-colors text-left">
      <span className={`${COUNTER} shrink-0 ${STATUS_BADGE[viewing.status] || ''}`}>{t('dv_status_' + viewing.status)}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{snap.address || '—'}</div>
        <div className="text-xs text-muted-foreground truncate">
          {dealTypeLabel(viewing.deal_type)}{names.length ? ` · ${names.join(', ')}` : ''}{viewing.client_name ? ` · ${viewing.client_name}` : ''}
        </div>
      </div>
      <div className="text-right shrink-0">
        {viewing.status === 'scheduled' && sched?.event_at ? (
          <div className={`text-xs font-medium ${overdue ? 'text-red-600' : 'text-blue-600 dark:text-blue-400'}`}>
            {overdue ? '⏰ ' : ''}{fmtDateTime(sched)}
          </div>
        ) : (
          <div className="text-sm font-medium text-emerald-600 dark:text-emerald-400">{fmtMoney(viewing.commission_amount, viewing.commission_currency)}</div>
        )}
        <div className="text-xs text-muted-foreground">{fmtDate(viewing.created_at)}</div>
      </div>
    </button>
  )
}

const DV_SORTS = ['newest', 'scheduled', 'comm_high']

// ─── Main tab ────────────────────────────────────────────────────────────────
export function DealsTab({ agents, allAgents = null, role = 'agent' }) {
  const [viewings, setViewings] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)   // viewing being edited
  const [creating, setCreating] = useState(false)
  const [status, setStatus] = useState('all')
  const [dtype, setDtype]   = useState('all')
  const [sort, setSort]     = useState('newest')
  const [search, setSearch] = useState('')
  const [view, setView]     = useViewPreference('deals_view')

  // CORE + legacy, one list (owner 2026-08-04: "anything there must show here"). This tab
  // read the scheduler `viewings` table alone, so it showed 189 rows while core held 244 —
  // every meeting booked through the Desk since the core cutover was invisible HERE, on the
  // page named for deals. getMeetingsFeed() is the same merged, backfill-deduped source the
  // Desk already uses; there is no second definition of "the meetings" now.
  // ⚠️ A FAILED FETCH IS NOT AN EMPTY BOOK (fixed 2026-08-15). `.catch(() => setViewings([]))`
  // rendered a total outage as "No deals yet" — and the status counts above were then computed
  // over the empty list as if they were complete. `loadErr` keeps the two apart.
  const [loadErr, setLoadErr] = useState('')
  // What the Desk card needs beyond the row (owner 2026-09-06: the grid draws the Desk's
  // meeting card verbatim): photo strips per property, district names, feature vocabulary
  // and the follow-up nudges. Each is cosmetic on failure — the card still renders.
  const [thumbsByProp, setThumbsByProp] = useState({})
  const [districts, setDistricts] = useState([])
  const [features, setFeatures] = useState([])
  const [nudges, setNudges] = useState(null)
  useEffect(() => {
    getDistricts().then(setDistricts).catch(() => {})
    getFeatures().then(setFeatures).catch(() => {})
    getMeetingNudges().then(setNudges).catch(() => {})
  }, [])
  const reload = () => {
    setLoading(true); setLoadErr('')
    getMeetingsFeed()
      .then(rows => { setViewings(rows); setLoadErr('') })
      .catch(e => { setViewings([]); setLoadErr(e?.message || String(e)) })
      .finally(() => setLoading(false))
  }
  useEffect(reload, [])
  // Photo strips for the cards, one representative listing per property, newest first —
  // the same 60-cap fetch the Desk makes (the endpoint's own cap).
  useEffect(() => {
    const seen = new Set()
    const ids = []
    for (const v of viewings) {
      const pid = v.property_snapshot?.core_property_id
      if (pid && !seen.has(pid)) { seen.add(pid); ids.push(pid) }
      if (ids.length >= 60) break
    }
    if (!ids.length) return
    let gone = false
    getPropertyThumbs(ids).then(({ thumbs }) => { if (!gone) setThumbsByProp(prev => ({ ...prev, ...thumbs })) }).catch(() => {})
    return () => { gone = true }
  }, [viewings])

  const schedAt = v => (v.events || []).find(e => e.kind === 'scheduled' || e.kind === 'reschedule')?.event_at

  const filtered = useMemo(() => {
    let list = viewings
    if (status !== 'all') list = list.filter(v => v.status === status)
    if (dtype !== 'all')  list = list.filter(v => v.deal_type === dtype)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(v => {
        const snap = v.property_snapshot || {}
        const names = (v.agent_ids || []).map(id => agents.find(a => a.id === id)?.name || '').join(' ').toLowerCase()
        // A core meeting carries no client NAME by design (it owns relationships, not
        // contacts), so searching only name/phone would make every core row unfindable.
        // Its representatives and district are what it does say — search those too.
        return (snap.address || '').toLowerCase().includes(q)
          || (snap.district_code || '').toLowerCase().includes(q)
          || (v.client_name || '').toLowerCase().includes(q)
          || (v.client_phone || '').includes(q) || (v.owner_phone || '').includes(q)
          || (v.client_representative?.name || '').toLowerCase().includes(q)
          || (v.property_representative?.name || '').toLowerCase().includes(q)
          || names.includes(q) || (v.notes || '').toLowerCase().includes(q)
      })
    }
    const arr = [...list]
    if (sort === 'comm_high')     arr.sort((a, b) => (Number(b.commission_amount) || 0) - (Number(a.commission_amount) || 0))
    else if (sort === 'scheduled') arr.sort((a, b) => (schedAt(b) || '').localeCompare(schedAt(a) || ''))
    else                           arr.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    return arr
  }, [viewings, status, dtype, sort, search, agents])

  function closeForm() { setEditing(null); setCreating(false) }
  function onSaved() { reload(); if (creating) setCreating(false) }

  return (
    <div className="space-y-3">
      {/* Toolbar — row 1: view toggle + search + add; row 2: status / type / sort */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <ViewToggle view={view} setView={setView} />
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"><Icon name="search" /></span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('dv_search')}
              className="w-full rounded-full border border-input pl-8 pr-3 py-1.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <button onClick={() => setCreating(true)}
            className="flex items-center gap-1 shrink-0 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity whitespace-nowrap">
            + {t('dv_new')}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <SingleSelect value={status} onChange={setStatus} options={[
            { value: 'all', label: `${t('stats_deals')} (${viewings.length})` },
            // A legacy alias appears ONLY when a row actually carries it — otherwise the
            // dropdown grows a permanent dead option that always reads (0).
            ...FINDABLE_STATUSES
              .filter(s => !LEGACY_STATUS_ALIASES.includes(s) || viewings.some(v => v.status === s))
              .map(s => ({ value: s, label: `${t('dv_status_' + s)} (${viewings.filter(v => v.status === s).length})` })),
          ]} />
          <SingleSelect value={dtype} onChange={setDtype} options={[
            { value: 'all', label: t('dv_type_all') },
            { value: 'lease', label: t('dv_deal_type_lease') },
            { value: 'trade', label: t('dv_deal_type_trade') },
          ]} />
          <SingleSelect value={sort} onChange={setSort} options={DV_SORTS.map(s => ({ value: s, label: t('dv_sort_' + s) }))} />
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground text-sm">{t('stats_loading')}</div>
      ) : loadErr ? (
        // The list could not be read. Saying "No deals yet" here claims the book is empty on
        // the strength of a request that failed — and the counts above would agree with it.
        <div className="py-16 text-center text-sm">
          <p className="text-destructive">{t('desk_load_failed')}</p>
          <button onClick={reload} className="mt-2 text-xs text-primary hover:underline">{t('dv_retry')}</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground text-sm">{t('dv_none')}</div>
      ) : view === 'card' ? (
        <>
          <MeetingCardGrid viewings={filtered} agents={allAgents || agents} thumbsByProp={thumbsByProp}
            districts={districts} features={features} nudges={nudges} onOpen={setEditing}
            onChanged={() => { reload(); getMeetingNudges().then(setNudges).catch(() => {}) }} />
        </>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {filtered.map(v => <ViewingRow key={v.id} viewing={v} agents={agents} onClick={() => setEditing(v)} />)}
        </div>
      )}

      {/* ⚠️ A CORE MEETING MUST NEVER REACH ViewingForm. That form writes through
          updateViewing(viewing.id, …) to the scheduler `viewings` table, and a core row's id
          is `core:<uuid>` — no such row exists, so the save fails silently (or, one day,
          matches something else). Core rows open the core detail modal, which is the only
          thing that can edit them; the Desk has carried this same guard since the cutover. */}
      {editing?.__core ? (
        <CoreMeetingDetail meeting={editing} agents={agents} role={role}
          onClose={closeForm} onChanged={reload} />
      ) : (creating && !editing) ? (
        // "+ New" books a CORE meeting (review 2026-09-23). Core is the only meeting store
        // (core/MEETINGS_MODEL.md); this used to open ViewingForm, whose create path writes a
        // scheduler `viewings` row via addViewing. MeetingModal starts empty and picks the
        // property/client itself. ViewingForm below now only EDITS legacy rows.
        <MeetingModal seed={{}} onClose={closeForm}
          onCreated={() => { setCreating(false); reload() }} />
      ) : editing ? (
        <ViewingForm key={editing?.id || 'new'} agents={agents} allAgents={allAgents} viewing={editing} allViewings={viewings}
          onClose={closeForm}
          onOpen={(v) => { setCreating(false); setEditing(v) }}
          onSaved={() => { reload(); if (creating) setCreating(false) }} />
      ) : null}
    </div>
  )
}
